import { computed, nextTick, ref, watch, type ComputedRef, type Ref } from 'vue';
import type {
  CellId,
  CellNavigationIntent,
  EditCellRegisterFocus,
  FocusFrom,
  VirtualWindow,
} from './types';
import {
  FILTER_ROW_ID,
  INDICATOR_COLUMN_ID,
  ROW_ACTION_COLUMN_ID,
  SELECT_COLUMN_ID,
  EXPAND_COLUMN_ID,
  SETTINGS_ROW_ID,
} from '../internal';
import type { TableSelection } from './useTableSelection';

export type TableMode = 'view' | 'edit';

export type Cursor = {
  rowId: string;
  rowIdx: number;
  columnId: string;
  columnIdx: number;
};

const focusKey = (rowId: string, columnId: string) => JSON.stringify([rowId, columnId]);

export type NavigateDelta = { row: -1 | 0 | 1; col: -1 | 0 | 1 };

export type FocusDir = 'above' | 'below' | 'left' | 'right';

/**
 * Hooks into directional focus navigation for table-level / DOM-level
 * concerns the composable doesn't know about (the table's wrapper
 * element, the row-action button waypoint, and row-creation).
 */
export type NavigateFocusHooks = {
  /** Past the last row when going down/right. Use to trigger row creation. */
  onPastLastRow?: () => void;
  /** Above the first row, no filter row available, or top of filter row. */
  onEscapeAbove?: () => void;
  /** Opt-in: append/focus a row when Tab passes the final body editor. */
  onTabPastLastRow?: () => boolean;
  /** Move Tab/Shift+Tab to the adjacent page tab stop outside the grid. */
  onTabEscape?: (direction: -1 | 1) => boolean;
};

export function useTableNavigation<T extends Record<string, unknown>>(params: {
  data: ComputedRef<T[]>;
  rowIdKey: (value: T) => string;
  mode: ComputedRef<TableMode>;
  /** If true, click handlers and cursor mutations are ignored (e.g. during row-creation flow). */
  ignoreClicks: ComputedRef<boolean>;
  visibleColumnOrder: ComputedRef<string[]>;

  offsetOf: (index: number) => number;
  virtualWindow: ComputedRef<VirtualWindow>;
  scrollTop: Ref<number>;
  tableRef: Ref<HTMLElement | undefined>;
  tableHeadRef: Ref<HTMLElement | undefined>;
  detectorRef: Ref<HTMLElement | undefined>;
  firstColRef: Ref<HTMLElement | undefined>;
  lastColRef: Ref<HTMLElement | undefined>;
  /**
   * Pixels at the scroller's left edge that stay covered by pinned cells of
   * `row` (indicator, checkbox and pinned data columns) — a cell scrolled into
   * view has to clear them. 0 for a pinned cell itself. Without it only the
   * indicator column is cleared.
   */
  leadingInset?: (row: Element | null, columnId: string) => number;
  /** Rendered bottom overlay (e.g. a sticky summary), outside row navigation. */
  bottomInset?: Readonly<Ref<number>>;

  /** Resolve a rendered cell inside this table instance. */
  getCellElement?: (rowId: string, columnId: string) => HTMLElement | null;
  /** True when a cell can become focusable after cursor-driven lazy mounting. */
  isCellNavigable?: (rowId: string, columnId: string) => boolean;
  /** Optional narrower predicate for the spreadsheet-style Tab sequence. */
  isCellTabNavigable?: (rowId: string, columnId: string) => boolean;

  selection: TableSelection<T>;

  onCellSingleClick: (rowId: string, columnId: string, rowData: T, event?: MouseEvent) => void;
  onCellDoubleClick: (rowId: string, columnId: string, rowData: T) => void;

  /**
   * Default hooks for `navigateFocus` / `getCellNav`. The table provides
   * DOM-bound concerns (row-action button waypoint, wrapper focus,
   * row creation) once; per-cell call sites don't pass them again.
   */
  focusHooks?: NavigateFocusHooks;
  /** Skip windowed/sparse shells when walking up/down. */
  isPlaceholderRow?: (row: T) => boolean;
}) {
  const cursor = ref<Cursor | null>(null);
  const focusTransitionPending = ref(false);
  let focusTransitionSerial = 0;
  let focusTransitionTarget: { rowId: string; columnId: string } | null = null;

  // EditCell focus registry: one handler per cell. The cell registers a
  // function `(from: FocusFrom) => void` and is responsible for focusing
  // the right input + caret/selection placement. The table only knows
  // *that* a cell can be focused, not *how*.
  const cellFocusHandlers = new Map<string, (from: FocusFrom) => void>();
  const registerFocusByCell = new Map<string, EditCellRegisterFocus>();
  const cellNavByCell = new Map<string, CellNav>();

  function getRegisterFocus(rowId: string, columnId: string): EditCellRegisterFocus {
    const k = focusKey(rowId, columnId);
    let fn = registerFocusByCell.get(k);
    if (!fn) {
      let registeredHandler: ((from: FocusFrom) => void) | null = null;
      fn = (handler) => {
        if (handler) {
          registeredHandler = handler;
          cellFocusHandlers.set(k, handler);
          return;
        }
        // A replacement editor may already have registered under the same
        // logical cell before the old component's ref teardown runs. Never let
        // that stale teardown remove the replacement handler.
        if (registeredHandler && cellFocusHandlers.get(k) === registeredHandler) {
          cellFocusHandlers.delete(k);
        }
        registeredHandler = null;
        if (registerFocusByCell.get(k) === fn) {
          registerFocusByCell.delete(k);
          cellNavByCell.delete(k);
        }
      };
      registerFocusByCell.set(k, fn);
    }
    return fn;
  }
  function isPlaceholderAt(index: number): boolean {
    const row = params.data.value[index];
    return !!row && params.isPlaceholderRow?.(row) === true;
  }

  /** Next non-placeholder index in `dir`, or -1. */
  function nextRealRowIdx(from: number, dir: 1 | -1): number {
    const n = params.data.value.length;
    for (let i = from + dir; i >= 0 && i < n; i += dir) {
      if (!isPlaceholderAt(i)) return i;
    }
    return -1;
  }

  function isCellFocusable(rowId: string, columnId: string): boolean {
    return cellFocusHandlers.has(focusKey(rowId, columnId));
  }
  function isCellNavigable(rowId: string, columnId: string): boolean {
    return isCellFocusable(rowId, columnId) || !!params.isCellNavigable?.(rowId, columnId);
  }
  function isCellTabNavigable(rowId: string, columnId: string): boolean {
    return (
      isCellNavigable(rowId, columnId) && (params.isCellTabNavigable?.(rowId, columnId) ?? true)
    );
  }
  function callCellFocus(rowId: string, columnId: string, from: FocusFrom): boolean {
    const h = cellFocusHandlers.get(focusKey(rowId, columnId));
    if (!h) return false;
    h(from);
    return true;
  }

  /** Row selector, checkbox, expand toggle and row actions: chrome columns, not content. */
  function isVirtualColumn(columnId: string): boolean {
    return (
      columnId === INDICATOR_COLUMN_ID ||
      columnId === SELECT_COLUMN_ID ||
      columnId === EXPAND_COLUMN_ID ||
      columnId === ROW_ACTION_COLUMN_ID
    );
  }

  // First focusable column in the row (left-to-right sweep). -1 if none.
  function firstFocusableColIdx(rowId: string, skipVirtual: boolean = false): number {
    const cols = params.visibleColumnOrder.value;
    for (let i = 0; i < cols.length; i++) {
      if (skipVirtual && isVirtualColumn(cols[i])) {
        continue;
      }
      if (isCellNavigable(rowId, cols[i])) return i;
    }
    return -1;
  }

  // Walk visible columns of `rowId` from `fromColIdx + dir` in `dir`
  // direction (1 forward, -1 backward) and return the first focusable
  // column index. -1 if none.
  function nextFocusableColIdx(rowId: string, fromColIdx: number, dir: 1 | -1): number {
    const cols = params.visibleColumnOrder.value;
    for (let i = fromColIdx + dir; i >= 0 && i < cols.length; i += dir) {
      if (isCellNavigable(rowId, cols[i])) return i;
    }
    return -1;
  }

  /** Tab walks editable/content cells only; row selectors and actions are not data-entry stops. */
  function nextTabColIdx(rowId: string, fromColIdx: number, dir: 1 | -1): number {
    const cols = params.visibleColumnOrder.value;
    for (let i = fromColIdx + dir; i >= 0 && i < cols.length; i += dir) {
      if (isVirtualColumn(cols[i])) continue;
      if (isCellTabNavigable(rowId, cols[i])) return i;
    }
    return -1;
  }

  // Sets the cursor to (rowId, columnId), scrolls it into view, and
  // calls the cell's registered focus-handler with the given hint. If
  // the cell has no handler, the cursor still moves but no focus lands.
  // Scroll is awaited so virtual-list rows scrolled into view get
  // mounted (and their focus handlers registered) before we focus.
  async function focusCell(rowId: string, columnId: string, from: FocusFrom): Promise<void> {
    const transition = ++focusTransitionSerial;
    focusTransitionTarget = { rowId, columnId };
    focusTransitionPending.value = true;
    setCursor(rowId, columnId);
    const isCurrent = () =>
      transition === focusTransitionSerial &&
      cursor.value?.rowId === rowId &&
      cursor.value.columnId === columnId;
    if (!isCurrent()) {
      if (transition === focusTransitionSerial) {
        focusTransitionTarget = null;
        focusTransitionPending.value = false;
      }
      return;
    }
    try {
      await scrollCursorIntoView();
      // Lazy-edit grids mount the editCell only after cursor lands. Wait for its
      // explicit focus registration instead of relying on a single lucky tick;
      // virtualization and async column components may need another paint.
      for (let attempt = 0; attempt < 4; attempt++) {
        await nextTick();
        await new Promise<void>((r) => requestAnimationFrame(() => r()));
        if (!isCurrent()) return;
        if (callCellFocus(rowId, columnId, from)) break;
      }
      if (!isCurrent()) return;
      // Edit widgets can grow the row (last visible line) after focus — pull
      // the cell back into the viewport so the last column doesn't sit under
      // the fold.
      await nextTick();
      await scrollCursorIntoView();
    } finally {
      if (transition === focusTransitionSerial) {
        focusTransitionTarget = null;
        focusTransitionPending.value = false;
      }
    }
  }

  function focusTabCell(rowId: string, columnIdx: number, direction: -1 | 1): true {
    void focusCell(
      rowId,
      params.visibleColumnOrder.value[columnIdx],
      direction === 1 ? 'top' : 'tab-back'
    );
    return true;
  }

  /**
   * Shift+Tab into the grid from the page after it: the last editor of the last
   * row, as a Shift+Tab inside the grid would reach it (`tab-back`). False when
   * no body cell takes Tab.
   */
  function focusLastTabCell(): boolean {
    const target = findBodyTabTarget(params.data.value.length, -1);
    if (!target) return false;
    return focusTabCell(target.rowId, target.columnIdx, -1);
  }

  /**
   * Tab into the grid from the page before it: the first editor of the first
   * row — the cell a Shift+Tab walk leaves the grid from, so both directions
   * share the boundary (I-2833). False when no body cell takes Tab.
   */
  function focusFirstTabCell(): boolean {
    const target = findBodyTabTarget(-1, 1);
    if (!target) return false;
    return focusTabCell(target.rowId, target.columnIdx, 1);
  }

  /**
   * Whether a view grid's Tab walk runs through its body: it does when the
   * filter row takes Tab, whose last cell hands Tab on to the first body cell.
   */
  function viewTabWalksBody(): boolean {
    return (
      params.mode.value === 'view' &&
      nextTabColIdx(FILTER_ROW_ID, -1, 1) >= 0 &&
      findBodyTabTarget(-1, 1) !== null
    );
  }

  function findBodyTabTarget(
    fromRowIdx: number,
    direction: -1 | 1
  ): { rowId: string; columnIdx: number } | null {
    let rowIdx = nextRealRowIdx(fromRowIdx, direction);
    while (rowIdx >= 0) {
      const rowId = params.rowIdKey(params.data.value[rowIdx]);
      const columnIdx =
        direction === 1
          ? nextTabColIdx(rowId, -1, 1)
          : nextTabColIdx(rowId, params.visibleColumnOrder.value.length, -1);
      if (columnIdx >= 0) return { rowId, columnIdx };
      rowIdx = nextRealRowIdx(rowIdx, direction);
    }
    return null;
  }

  /**
   * Spreadsheet Tab order is deliberately separate from arrow navigation:
   * it visits content editors row-by-row and never row selectors/actions. An
   * opted-in entry grid may append at the forward boundary; otherwise focus is
   * handed to the surrounding page.
   */
  function navigateTab(fromRowId: string, fromColumnId: string, direction: -1 | 1): boolean {
    const cols = params.visibleColumnOrder.value;
    const colIdx = cols.indexOf(fromColumnId);
    if (colIdx === -1) return false;

    const isSettings = fromRowId === SETTINGS_ROW_ID;
    const isFilter = fromRowId === FILTER_ROW_ID;

    if (!isSettings) {
      const sameRowTarget = nextTabColIdx(fromRowId, colIdx, direction);
      if (sameRowTarget >= 0) return focusTabCell(fromRowId, sameRowTarget, direction);
    }

    if (isSettings) {
      if (direction === -1) return params.focusHooks?.onTabEscape?.(-1) ?? false;
      const filterTarget = nextTabColIdx(FILTER_ROW_ID, -1, 1);
      if (filterTarget >= 0) return focusTabCell(FILTER_ROW_ID, filterTarget, 1);
      const bodyTarget = findBodyTabTarget(-1, 1);
      return bodyTarget
        ? focusTabCell(bodyTarget.rowId, bodyTarget.columnIdx, 1)
        : (params.focusHooks?.onTabEscape?.(1) ?? false);
    }

    if (isFilter) {
      // A view grid's filter row is entered by the browser's own Tab from the
      // header controls before it, so Shift+Tab goes back there natively —
      // the same stops in reverse (I-2833). An edit grid leaves for the page.
      if (direction === -1 && params.mode.value === 'view') return false;
      if (direction === -1) return params.focusHooks?.onTabEscape?.(-1) ?? false;
      const bodyTarget = findBodyTabTarget(-1, 1);
      return bodyTarget
        ? focusTabCell(bodyTarget.rowId, bodyTarget.columnIdx, 1)
        : (params.focusHooks?.onTabEscape?.(1) ?? false);
    }

    const rowIdx = params.data.value.findIndex((row) => params.rowIdKey(row) === fromRowId);
    if (rowIdx < 0) return false;
    const bodyTarget = findBodyTabTarget(rowIdx, direction);
    if (bodyTarget) return focusTabCell(bodyTarget.rowId, bodyTarget.columnIdx, direction);
    if (direction === -1 && params.mode.value === 'view') {
      // Mirror of the filter row's Tab onto the first body cell (I-2833).
      const filterTarget = nextTabColIdx(FILTER_ROW_ID, cols.length, -1);
      if (filterTarget >= 0) return focusTabCell(FILTER_ROW_ID, filterTarget, -1);
    }
    if (direction === 1 && params.focusHooks?.onTabPastLastRow?.()) return true;
    return params.focusHooks?.onTabEscape?.(direction) ?? false;
  }

  function defaultEscapeAbove() {
    const el = document.activeElement as HTMLElement | null;
    el?.blur?.();
  }

  /**
   * Directional focus move from a starting cell. Understands `FILTER_ROW_ID`
   * as a virtual row domain above body row 0 (rowIdx === -1). ArrowUp from
   * body row 0 lands on the filter row if any column has a focus handler;
   * ArrowDown/right-wrap from filter row enters body row 0. Body indexing
   * stays 0..N-1.
   */
  function navigateFocus(
    fromRowId: string,
    fromColumnId: string,
    dir: FocusDir,
    hooks: NavigateFocusHooks = params.focusHooks ?? {}
  ): void {
    const cols = params.visibleColumnOrder.value;
    const colIdx = cols.indexOf(fromColumnId);
    if (colIdx === -1) return;

    const isSettings = fromRowId === SETTINGS_ROW_ID;
    const isFilter = fromRowId === FILTER_ROW_ID;
    const rowIdx =
      isFilter || isSettings
        ? -1
        : params.data.value.findIndex((r) => params.rowIdKey(r) === fromRowId);
    if (!isSettings && !isFilter && rowIdx === -1) return;

    const escape = hooks.onEscapeAbove ?? defaultEscapeAbove;

    if (dir === 'above') {
      if (isSettings) {
        escape();
        return;
      }
      if (isFilter) {
        // go to settings
        const settingsIdx = firstFocusableColIdx(SETTINGS_ROW_ID);
        if (settingsIdx >= 0) {
          focusCell(SETTINGS_ROW_ID, cols[settingsIdx], 'bottom');
          return;
        }
        escape();
        return;
      }
      const previousIdx = nextRealRowIdx(rowIdx, -1);
      if (previousIdx < 0) {
        // From first body row → filter row if any column has a filter handler.
        const filterFbIdx = firstFocusableColIdx(FILTER_ROW_ID);
        if (filterFbIdx >= 0) {
          const targetCol = isCellNavigable(FILTER_ROW_ID, fromColumnId)
            ? fromColumnId
            : cols[filterFbIdx];
          focusCell(FILTER_ROW_ID, targetCol, 'bottom');
          return;
        }
        const settingsIdx = firstFocusableColIdx(SETTINGS_ROW_ID);
        if (settingsIdx >= 0) {
          focusCell(SETTINGS_ROW_ID, cols[settingsIdx], 'bottom');
          return;
        }
        escape();
        return;
      }
      const newRowId = params.rowIdKey(params.data.value[previousIdx]);
      if (isCellNavigable(newRowId, fromColumnId)) {
        focusCell(newRowId, fromColumnId, 'bottom');
        return;
      }
      const fb = firstFocusableColIdx(newRowId);
      if (fb >= 0) {
        focusCell(newRowId, cols[fb], 'bottom');
        return;
      }
      escape();
      return;
    }

    if (dir === 'below') {
      if (isSettings) {
        // go to 1st filter if available
        const filterIdx = firstFocusableColIdx(FILTER_ROW_ID);
        if (filterIdx >= 0) {
          focusCell(FILTER_ROW_ID, cols[filterIdx], 'bottom');
          return;
        }
        // otherwise go to 1st row
        if (params.data.value.length === 0) return;
        const newRowId = params.rowIdKey(params.data.value[0]);
        const firstRowIdx = firstFocusableColIdx(newRowId);
        if (firstRowIdx >= 0) focusCell(newRowId, cols[firstRowIdx], 'top');
        return;
      }
      if (isFilter) {
        // From filter row → first body row.
        if (params.data.value.length === 0) return;
        // Note: empty data does NOT trigger onPastLastRow here. The filter row is
        // itself the destination of "below" only conceptually; row creation from
        // filter row is not a defined gesture.
        const newRowId = params.rowIdKey(params.data.value[0]);
        if (isCellNavigable(newRowId, fromColumnId)) {
          focusCell(newRowId, fromColumnId, 'top');
          return;
        }
        const fb = firstFocusableColIdx(newRowId);
        if (fb >= 0) focusCell(newRowId, cols[fb], 'top');
        return;
      }
      const nextIdx = nextRealRowIdx(rowIdx, 1);
      if (nextIdx < 0) {
        // Placeholders after the last real row are unloaded tail — do not create.
        if (rowIdx + 1 >= params.data.value.length) hooks.onPastLastRow?.();
        return;
      }
      const newRowId = params.rowIdKey(params.data.value[nextIdx]);
      if (isCellNavigable(newRowId, fromColumnId)) {
        focusCell(newRowId, fromColumnId, 'top');
        return;
      }
      const fb = firstFocusableColIdx(newRowId);
      if (fb >= 0) focusCell(newRowId, cols[fb], 'top');
      return;
    }

    if (dir === 'left') {
      const targetIdx = nextFocusableColIdx(fromRowId, colIdx, -1);
      if (targetIdx >= 0) {
        focusCell(fromRowId, cols[targetIdx], 'bottom');
        return;
      }
      // Left-edge: wrap to previous row's last focusable. Filter row → escape
      // (filter row is the topmost domain). Body row 0 → filter row's last
      // focusable, else escape. Mirror of right-edge wrap; lands on last
      // focusable (does NOT prefer fromColumnId — caret-flow analogy).
      if (isSettings) {
        escape();
        return;
      }
      if (isFilter) {
        // go to settings
        const settingsIdx = firstFocusableColIdx(SETTINGS_ROW_ID);
        if (settingsIdx >= 0) {
          focusCell(SETTINGS_ROW_ID, cols[settingsIdx], 'bottom');
          return;
        }
        escape();
        return;
      }
      const prevIdx = nextRealRowIdx(rowIdx, -1);
      if (prevIdx < 0) {
        const filterLastIdx = nextFocusableColIdx(FILTER_ROW_ID, cols.length, -1);
        if (filterLastIdx >= 0) {
          focusCell(FILTER_ROW_ID, cols[filterLastIdx], 'bottom');
          return;
        }
        const settingsIdx = firstFocusableColIdx(SETTINGS_ROW_ID);
        if (settingsIdx >= 0) {
          focusCell(SETTINGS_ROW_ID, cols[settingsIdx], 'bottom');
          return;
        }
        escape();
        return;
      }
      const newRowId = params.rowIdKey(params.data.value[prevIdx]);
      const lb = nextFocusableColIdx(newRowId, cols.length, -1);
      if (lb < 0) return;
      const landIdx =
        cols[lb] === ROW_ACTION_COLUMN_ID ? nextFocusableColIdx(newRowId, lb, -1) : lb;
      if (landIdx >= 0) focusCell(newRowId, cols[landIdx], 'bottom');
      return;
    }

    // dir === 'right'
    let targetIdx = nextFocusableColIdx(fromRowId, colIdx, 1);
    // Delete/X is clickable, not a Tab/Enter stop — wrap or create instead.
    if (targetIdx >= 0 && cols[targetIdx] === ROW_ACTION_COLUMN_ID) {
      targetIdx = -1;
    }
    if (targetIdx >= 0) {
      focusCell(fromRowId, cols[targetIdx], 'top');
      return;
    }
    // Right-edge: wrap to next row's first content field (skip checkbox / X)
    if (isSettings) {
      // go to 1st filter if available
      const filterIdx = firstFocusableColIdx(FILTER_ROW_ID);
      if (filterIdx >= 0) {
        focusCell(FILTER_ROW_ID, cols[filterIdx], 'bottom');
      } else {
        // otherwise go to 1st row
        if (params.data.value.length === 0) return;
        const newRowId = params.rowIdKey(params.data.value[0]);
        const firstRowIdx = firstFocusableColIdx(newRowId);
        if (firstRowIdx >= 0) focusCell(newRowId, cols[firstRowIdx], 'top');
      }
      return;
    }
    // Filter row → first body row.
    if (isFilter) {
      // Right-edge wrap from filter row always lands on the first focusable
      // column of body row 0 — it does NOT prefer fromColumnId. (Below from
      // filter row DOES prefer fromColumnId, because vertical motion implies
      // staying in the same column.)
      if (params.data.value.length === 0) return;
      const newRowId = params.rowIdKey(params.data.value[0]);
      const fb = firstFocusableColIdx(newRowId);
      if (fb >= 0) focusCell(newRowId, cols[fb], 'top');
      return;
    }
    const nextIdx = nextRealRowIdx(rowIdx, 1);
    if (nextIdx < 0) {
      if (rowIdx + 1 >= params.data.value.length) hooks.onPastLastRow?.();
      return;
    }
    const newRowId = params.rowIdKey(params.data.value[nextIdx]);
    const fb = firstFocusableColIdx(newRowId, true);
    if (fb >= 0) {
      focusCell(newRowId, cols[fb], 'top');
      return;
    }
    const any = firstFocusableColIdx(newRowId);
    if (any >= 0) focusCell(newRowId, cols[any], 'top');
  }

  const cursorRow = computed(() => {
    const c = cursor.value;
    if (!c || c.rowId === FILTER_ROW_ID || c.rowId === SETTINGS_ROW_ID) return null;
    return params.data.value[c.rowIdx] ?? null;
  });
  const isKeyboardActive = computed(() => cursor.value !== null);

  function isCursor(rowId: string, columnId?: string): boolean {
    const c = cursor.value;
    if (!c) return false;
    if (c.rowId !== rowId) return false;
    if (columnId === undefined) return true;
    return c.columnId === columnId;
  }

  function setCursor(rowId: string, columnId: string) {
    const rowIdx =
      rowId === SETTINGS_ROW_ID
        ? -2
        : rowId === FILTER_ROW_ID
          ? -1
          : params.data.value.findIndex((v) => params.rowIdKey(v) === rowId);
    if (rowId !== SETTINGS_ROW_ID && rowId !== FILTER_ROW_ID && rowIdx === -1) return;
    const colIdx = params.visibleColumnOrder.value.indexOf(columnId);
    if (colIdx === -1) return;
    cursor.value = { rowId, rowIdx, columnId, columnIdx: colIdx };
  }

  function clearCursor() {
    focusTransitionSerial++;
    focusTransitionTarget = null;
    focusTransitionPending.value = false;
    cursor.value = null;
  }

  // Any direct cursor move supersedes an in-flight lazy focus request. This
  // keeps a stale async transition from reclaiming focus after a newer click.
  watch(
    cursor,
    (current) => {
      if (!focusTransitionTarget) return;
      if (
        current?.rowId === focusTransitionTarget.rowId &&
        current.columnId === focusTransitionTarget.columnId
      ) {
        return;
      }
      focusTransitionSerial++;
      focusTransitionTarget = null;
      focusTransitionPending.value = false;
    },
    { flush: 'sync' }
  );

  async function scrollCursorIntoView() {
    const cur = cursor.value;
    if (!cur) return;
    // Settings and filter rows live in the thead area (not virtualized) —
    // skip body scroll.
    if (cur.rowId !== SETTINGS_ROW_ID && cur.rowId !== FILTER_ROW_ID) {
      const { start, end } = params.virtualWindow.value;
      if (params.tableRef.value && (cur.rowIdx < start || cur.rowIdx >= end)) {
        const headerH = params.tableHeadRef.value?.getBoundingClientRect().height ?? 0;
        params.tableRef.value.scrollTop = Math.max(0, params.offsetOf(cur.rowIdx) - headerH);
        params.scrollTop.value = params.tableRef.value.scrollTop;
        await nextTick();
      }
    }

    const colId = cur.columnId;
    const cellDiv = params.getCellElement?.(cur.rowId, colId) ?? null;
    if (!cellDiv) return;

    const rowEl = cellDiv.closest('.tr');
    const leftOffset = params.leadingInset
      ? params.leadingInset(rowEl, colId)
      : (params.firstColRef.value?.getBoundingClientRect().width ?? 0);
    const rightOffset = params.lastColRef.value?.getBoundingClientRect().width ?? 0;
    const topOffset = params.tableHeadRef.value?.getBoundingClientRect().height ?? 0;
    const bottomOffset = params.bottomInset?.value ?? 0;
    const cellDivRect = cellDiv.getBoundingClientRect();
    // Vertical: the whole row, not the cursor cell. The indicator / first
    // column stays short while lazy-edit Bezeichnung grows — using the cell
    // alone leaves the last line half under the fold.
    const rowRect = rowEl?.getBoundingClientRect() ?? cellDivRect;
    const wrapperRect = params.tableRef.value!.getBoundingClientRect();
    const yScrollbarWidth =
      params.tableRef.value!.offsetWidth - (params.detectorRef.value?.offsetWidth ?? 0);
    const xScrollbarWidth =
      params.tableRef.value!.offsetHeight - (params.detectorRef.value?.offsetHeight ?? 0);
    if (
      rowRect.top - topOffset >= wrapperRect.top &&
      cellDivRect.left - leftOffset >= wrapperRect.left &&
      rowRect.bottom + xScrollbarWidth + bottomOffset <= wrapperRect.bottom &&
      cellDivRect.right + rightOffset + yScrollbarWidth <= wrapperRect.right
    ) {
      return;
    }
    let xOffset = 0;
    let yOffset = 0;
    if (cellDivRect.right + rightOffset + yScrollbarWidth > wrapperRect.right) {
      xOffset = cellDivRect.right - wrapperRect.right + rightOffset + yScrollbarWidth;
    }
    if (rowRect.bottom + xScrollbarWidth + bottomOffset > wrapperRect.bottom) {
      yOffset = rowRect.bottom - wrapperRect.bottom + xScrollbarWidth + bottomOffset;
    }
    if (cellDivRect.left - leftOffset < wrapperRect.left) {
      xOffset = cellDivRect.left - wrapperRect.left - leftOffset;
    }
    if (rowRect.top - topOffset < wrapperRect.top) {
      yOffset = rowRect.top - wrapperRect.top - topOffset;
    }
    params.tableRef.value?.scrollTo({
      left: params.tableRef.value.scrollLeft + xOffset,
      top: params.tableRef.value.scrollTop + yOffset,
      behavior: 'instant', // 'smooth' make it feels laggy,
    });
  }

  // Click handling (view mode = single/double mux, edit mode = set cursor without mux)
  let timer: ReturnType<typeof setTimeout>;
  let pendingClick: { rowId: string; columnId: string; single: () => void } | null = null;

  /** Proxy to decide wether it was a double or single-click */
  function onCellClickProxy(cell: CellId, event: MouseEvent) {
    if (params.ignoreClicks.value) return;
    // Edit mode: editable cell padding activates that editor in one click;
    // computed/display-only cells retain a stable row-indicator focus anchor.
    // `[data-focus-group]` marks input trailing actions as part of the editor.
    const target = event.target as HTMLElement | null;
    const insideInput = !!target?.closest(
      'input, textarea, select, button, a[href], [role="button"], [role="combobox"], [contenteditable="true"], [data-focus-group]'
    );
    if (params.mode.value === 'edit') {
      if (isCellNavigable(cell.rowId, cell.columnId)) {
        cursor.value = { ...cell };
        // A lazy row initially paints display cells. One click must activate and
        // focus the requested editor; sending padding clicks to the row indicator
        // required an unintuitive second click into the actual input.
        if (!insideInput) void focusCell(cell.rowId, cell.columnId, 'click');
      } else {
        // Computed/display/blank cells do not own an editor focus handler. Keep
        // keyboard ownership on the row selector instead of leaving focus on
        // <body> with a cursor that can never move.
        setCursor(cell.rowId, INDICATOR_COLUMN_ID);
        callCellFocus(cell.rowId, INDICATOR_COLUMN_ID, 'click');
      }
    } else {
      cursor.value = { ...cell };
      callCellFocus(cell.rowId, cell.columnId, 'click');
    }
    // In edit mode, a plain cell click activates the editor only. Row selection
    // belongs to the left selector (or an explicit modifier-click); otherwise
    // the clicked row stays selected after ArrowDown moves the edit cursor and
    // two rows look active at once.
    if (params.mode.value === 'view' || event.ctrlKey || event.metaKey || event.shiftKey) {
      params.selection.toggleRowSelection(cell.rowIdx, {
        ctrlKey: event.ctrlKey || event.metaKey,
        shiftKey: event.shiftKey,
      });
    }
    scrollCursorIntoView();
    if (params.mode.value !== 'view') {
      // Same as focusCell: wait for lazy-edit to mount, then keep the
      // grown row (esp. last column) inside the scroller.
      void nextTick().then(() => {
        requestAnimationFrame(() => {
          void scrollCursorIntoView();
        });
      });
      return;
    }

    const row = params.data.value[cell.rowIdx];
    if (!row) return;
    const previous = pendingClick;
    const modifiesSelection = event.shiftKey || event.ctrlKey || event.metaKey || event.altKey;
    clearTimeout(timer);
    pendingClick = null;
    if (
      !modifiesSelection &&
      previous?.rowId === cell.rowId &&
      previous.columnId === cell.columnId
    ) {
      params.onCellDoubleClick(cell.rowId, cell.columnId, row);
      return;
    }
    // A second cell or a range/toggle click cannot open the previously clicked
    // row. Resolve its single click, then start a fresh cell-specific window.
    previous?.single();
    const single = () => params.onCellSingleClick(cell.rowId, cell.columnId, row, event);
    if (modifiesSelection) {
      single();
      return;
    }
    pendingClick = { rowId: cell.rowId, columnId: cell.columnId, single };
    timer = setTimeout(() => {
      pendingClick = null;
      single();
    }, 300);
  }

  function onRowClick(rowIdx: number, event: MouseEvent) {
    if (params.ignoreClicks.value) return;
    const target = event.target as HTMLElement | null;
    const insideCell = !!target?.closest('[data-row-id][data-column-id]');
    if (insideCell) return; // cell handler has already updated cursor + selection
    const row = params.data.value[rowIdx];
    if (!row) return;
    setCursor(params.rowIdKey(row), INDICATOR_COLUMN_ID);
    params.selection.toggleRowSelection(rowIdx, {
      ctrlKey: event.ctrlKey || event.metaKey,
      shiftKey: event.shiftKey,
    });
    callCellFocus(params.rowIdKey(row), INDICATOR_COLUMN_ID, 'click');
    void nextTick().then(() => {
      requestAnimationFrame(() => {
        void scrollCursorIntoView();
      });
    });
  }

  // Watches: keep cursor coherent across column-visibility and data mutations.
  watch(params.visibleColumnOrder, (newOrder) => {
    const c = cursor.value;
    if (!c) return;
    const idx = newOrder.indexOf(c.columnId);
    if (idx === -1) {
      // Column disappeared — fall back to indicator anchor.
      const indicatorIdx = newOrder.indexOf(INDICATOR_COLUMN_ID);
      if (indicatorIdx !== -1) {
        cursor.value = { ...c, columnId: INDICATOR_COLUMN_ID, columnIdx: indicatorIdx };
      } else {
        cursor.value = null;
      }
    } else if (idx !== c.columnIdx) {
      cursor.value = { ...c, columnIdx: idx };
    }
  });

  watch(
    () => params.data.value,
    (newData) => {
      const c = cursor.value;
      if (!c) return;
      // Header virtual rows are not tied to body data — leave them alone.
      if (c.rowId === FILTER_ROW_ID || c.rowId === SETTINGS_ROW_ID) return;
      const newRowIdx = newData.findIndex((v) => params.rowIdKey(v) === c.rowId);
      if (newRowIdx === -1) {
        cursor.value = null;
      } else {
        if (newRowIdx !== c.rowIdx) {
          cursor.value = { ...c, rowIdx: newRowIdx };
          scrollCursorIntoView();
        }
      }
    },
    { flush: 'post' }
  );

  type CellNav = {
    focusAbove: () => void;
    focusBelow: () => void;
    focusLeft: (intent?: CellNavigationIntent) => boolean;
    focusRight: (intent?: CellNavigationIntent) => boolean;
  };
  /**
   * Memoized per-cell directional focus closure. Edit cells receive
   * this as slot props; calling any of the four methods routes through
   * `navigateFocus`. Cursor movement deliberately does not mutate row
   * selection: active cell and selected rows are independent grid state.
   */
  function getCellNav(rowId: string, columnId: string): CellNav {
    const k = focusKey(rowId, columnId);
    let entry = cellNavByCell.get(k);
    if (!entry) {
      entry = {
        focusAbove: () => {
          navigateFocus(rowId, columnId, 'above');
        },
        focusBelow: () => {
          navigateFocus(rowId, columnId, 'below');
        },
        focusLeft: (intent) => {
          if (intent === 'tab') return navigateTab(rowId, columnId, -1);
          navigateFocus(rowId, columnId, 'left');
          return true;
        },
        focusRight: (intent) => {
          if (intent === 'tab') return navigateTab(rowId, columnId, 1);
          navigateFocus(rowId, columnId, 'right');
          return true;
        },
      };
      cellNavByCell.set(k, entry);
    }
    return entry;
  }

  return {
    cursor,
    focusTransitionPending,
    cursorRow,
    isCursor,
    setCursor,
    clearCursor,
    onCellClickProxy,
    onRowClick,
    scrollCursorIntoView,
    isKeyboardActive,
    getRegisterFocus,
    isCellFocusable,
    callCellFocus,
    firstFocusableColIdx,
    nextFocusableColIdx,
    focusCell,
    focusLastTabCell,
    focusFirstTabCell,
    viewTabWalksBody,
    navigateFocus,
    getCellNav,
  };
}
